import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { LoginService } from '../login/login.service';
import { Router } from '@angular/router';
import { catchError, switchMap } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable()
export class ResponseInterceptor implements HttpInterceptor {

  private sessionExpiredBannerOpen = false;

  constructor(
    private loginService: LoginService,
    private router: Router,
    private snackBar: MatSnackBar
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(catchError(err => {
      // A 401 never clears tokens automatically. It shows a banner; only the
      // user pressing "Log in again" triggers the explicit logout.
      // Only a genuine auth-token failure means the session is actually gone.
      // Other 401s (e.g. a single endpoint missing org context) must NOT
      // masquerade as a session expiry while the user is still logged in and
      // other calls succeed.
      // On the OIDC redirect page a code exchange is in flight, so any token the request
      // carried is the PREVIOUS session's and is about to be replaced. Refreshing there
      // would replay an equally stale refresh token -- an invalid_grant at best, and at
      // worst a reuse-detection hit that revokes the whole grant. The error still
      // propagates to the caller; it is only the "your session died" reading of it that
      // is wrong here. CrossTabTokenSync applies the same guard to its proactive refresh.
      // Skip refresh during forced teardown (`logout()`); `logoutGlobal()` still retries.
      if (
        err.status === 401 &&
        this.isSessionExpiry(err) &&
        this.loginService.isLoggedIn &&
        !this.loginService.exchangingCode &&
        !this.loginService.forcedTeardown &&
        !this.router.routerState.snapshot.url.includes('login')
      ) {
        // A refresh token still means the session is alive even though this
        // particular request's access token was rejected (clock skew, a
        // backgrounded tab's throttled timers, ...). Refresh and retry once
        // before treating it as an expired session -- the caller should never
        // see a 401 while a valid refresh token exists.
        if (this.loginService.hasRefreshToken) {
          return this.loginService.ensureFreshToken$().pipe(
            switchMap(() => {
              const retried = request.clone({
                setHeaders: { authorization: `Bearer ${this.loginService.access_token}` }
              });
              return next.handle(retried);
            }),
            catchError(() => {
              this.showSessionExpiredBanner();
              return throwError(err);
            })
          );
        }
        this.showSessionExpiredBanner();
      }
      return throwError(err);
    }))
  }

  // Matches the messages AuthGuard throws for a dead token
  // (apps/edu-web/src/auth/auth.guard.ts). Anything else is not a session expiry.
  private isSessionExpiry(err: unknown): boolean {
    const raw = (err as { error?: { message?: unknown } })?.error?.message;
    const message = Array.isArray(raw) ? raw.join(' ') : String(raw ?? '');
    return /token expired|invalid token|missing authorization header/i.test(message);
  }

  private showSessionExpiredBanner(): void {
    if (this.sessionExpiredBannerOpen) {
      return;
    }
    this.sessionExpiredBannerOpen = true;
    const ref = this.snackBar.open('Your session has expired.', 'Log in again', {
      verticalPosition: 'top',
      horizontalPosition: 'right'
    });
    ref.onAction().subscribe(async () => {
      await this.loginService.logout();
      // Full page load so all in-memory service state is discarded too.
      let url = this.router.routerState.snapshot.url;
      let redirect = url.includes('login') ? '' : `?redirect_to=${encodeURIComponent(url)}`;
      window.location.assign(`/login${redirect}`);
    });
    ref.afterDismissed().subscribe(() => {
      this.sessionExpiredBannerOpen = false;
    });
  }
}
